import { useAuthContext } from '../../auth/context';

export type UpgradeReason =
  | 'free'
  | 'none'
  | 'expired'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'trial_ending'
  | 'trial_over';

export type BannerSeverity = 'info' | 'warning' | 'danger';

export interface UpgradeState {
  /** Show the header "Upgrade plan" affordance. */
  showUpgrade: boolean;
  /** Why we're nudging — picked by priority (danger first). null when nothing to nudge. */
  reason: UpgradeReason | null;
  /** Days left in a trial (ceil); null when not trialing. Negative once the trial is over. */
  daysLeft: number | null;
  /** Severity for the persistent banner; null means no banner (header button only). */
  bannerSeverity: BannerSeverity | null;
}

const MS_PER_DAY = 86_400_000;

const NONE: UpgradeState = {
  showUpgrade: false,
  reason: null,
  daysLeft: null,
  bannerSeverity: null,
};

/** Prefers `entitlements.subscriptionStatus` over the org's, and stays inert until the profile loads. */
export function useUpgradeState(trialWarnDays = 3): UpgradeState {
  const { state } = useAuthContext();
  if (!state.isFetched) return NONE;

  const ent = state.entitlements;
  const org = state.organization;
  const status = ent?.subscriptionStatus ?? org?.subscriptionStatus ?? null;
  const planKey = ent?.planKey ?? org?.tier ?? null;
  const tier = org?.tier ?? null;
  const trialEndsAt = ent?.trialEndsAt;

  const daysLeft =
    status === 'trialing' && trialEndsAt
      ? Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / MS_PER_DAY)
      : null;

  // EXPIRED is the permanent Free plan an org lands on after the reverse trial
  // (the key is historical). It is a working plan, not a lockout, so it gets the
  // calm header nudge rather than a danger banner.
  const isFreeTier =
    planKey === 'FREE' || tier === 'FREE' || planKey === 'EXPIRED' || tier === 'EXPIRED';

  // Priority: danger → warning → info. The first match wins.
  if (status === 'past_due')
    return { showUpgrade: true, reason: 'past_due', daysLeft, bannerSeverity: 'danger' };
  if (status === 'unpaid')
    return { showUpgrade: true, reason: 'unpaid', daysLeft, bannerSeverity: 'danger' };
  if (daysLeft != null && daysLeft <= 0)
    return { showUpgrade: true, reason: 'trial_over', daysLeft, bannerSeverity: 'danger' };
  if (status === 'canceled')
    return { showUpgrade: true, reason: 'canceled', daysLeft, bannerSeverity: 'warning' };
  if (daysLeft != null && daysLeft <= trialWarnDays)
    return { showUpgrade: true, reason: 'trial_ending', daysLeft, bannerSeverity: 'warning' };
  // Free tier / no subscription: header button only, no persistent banner.
  if (isFreeTier || status === 'none')
    return {
      showUpgrade: true,
      reason: isFreeTier ? 'free' : 'none',
      daysLeft,
      bannerSeverity: null,
    };

  return NONE;
}
